import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import { useState } from "react"
import { pilotRequests, type ResearchRelease } from "./pilotRequestsApi"
export function ReleaseHistory({ requestId }: { requestId: string }) {
  const { user } = useUser()
  const { translate: t } = useTranslation()
  const rows = useInfiniteQuery({
    queryKey: ["company-index", user?.organization, "releases", requestId],
    queryFn: ({ pageParam }) => pilotRequests.releases(requestId, pageParam),
    initialPageParam: 0,
    getNextPageParam: (last, pages) =>
      last.length === 30 ? pages.length * 30 : undefined,
  })
  return (
    <section className="space-y-3">
      <h4 className="font-medium">{t("CI_DELIVERY_TITLE")}</h4>
      {rows.data?.pages
        .flat()
        .map((release) => (
          <ReleaseCard
            key={release.id}
            release={release}
            requestId={requestId}
          />
        ))}
      {rows.isError && <p role="alert">{t("CI_ERROR")}</p>}
      {rows.hasNextPage && (
        <Button
          variant="outline"
          disabled={rows.isFetchingNextPage}
          onClick={() => void rows.fetchNextPage()}
        >
          {t("CI_MORE")}
        </Button>
      )}
    </section>
  )
}
function ReleaseCard({
  release,
  requestId,
}: {
  release: ResearchRelease
  requestId: string
}) {
  const { user } = useUser()
  const { translate: t } = useTranslation()
  const client = useQueryClient()
  const [references, setReferences] = useState<Record<string, string>>({})
  const [confirmed, setConfirmed] = useState(false)
  const download = useMutation({
    mutationFn: () => pilotRequests.downloadRelease(release.id),
  })
  const save = useMutation({
    mutationFn: () =>
      pilotRequests.recordDelivery(
        release.id,
        release.items.map((item) => ({
          sha256: item.sha256,
          customerDeclarationReference: references[item.sha256].trim(),
        })),
      ),
    onSuccess: () => {
      void client.invalidateQueries({
        queryKey: ["company-index", user?.organization, "releases", requestId],
      })
    },
  })
  return (
    <div className="space-y-3 rounded border p-3">
      <p>
        {release.recipientOrganization} · {release.items.length}{" "}
        {t("CI_DELIVERY_FILES")}
      </p>
      <Button
        variant="outline"
        disabled={download.isPending}
        onClick={() => download.mutate()}
      >
        {t("CI_HANDOFF_DOWNLOAD")}
      </Button>
      {release.deliveryReceipt ? (
        <div>
          <p>
            {t("CI_DELIVERY_ATTESTED")} · {release.deliveryReceipt.actor} ·{" "}
            {new Date(release.deliveryReceipt.recordedAt).toLocaleString()}
          </p>
          {release.deliveryReceipt.documents.map((d) => (
            <p key={d.sha256} className="text-sm">
              {d.customerDeclarationReference}
            </p>
          ))}
        </div>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            {t("CI_DELIVERY_HINT")}
          </p>
          {release.items.map((item) => (
            <label key={item.sha256} className="block">
              {item.filename}
              <Input
                maxLength={200}
                value={references[item.sha256] ?? ""}
                disabled={save.isPending}
                onChange={(e) =>
                  setReferences({
                    ...references,
                    [item.sha256]: e.target.value,
                  })
                }
              />
            </label>
          ))}
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={confirmed}
              disabled={save.isPending}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            {t("CI_DELIVERY_CONFIRM")}
          </label>
          <Button
            disabled={
              save.isPending ||
              !confirmed ||
              !release.items.every((item) => references[item.sha256]?.trim())
            }
            onClick={() => save.mutate()}
          >
            {t("CI_DELIVERY_RECORD")}
          </Button>
        </>
      )}
      {(save.isError || download.isError) && (
        <p role="alert">{t("CI_HANDOFF_ERROR")}</p>
      )}
    </div>
  )
}
